import type { TFunction } from 'i18next'
import type { ApiError } from '@/components/features/bookings/bookingsUtils'
import type { NetworkingBadge, NetworkingCategory, NetworkingConfig, NetworkingPreset } from '@/types/networking'

export const PRESETS: NetworkingPreset[] = ['COMMUNITY_MENTORING', 'COMPANY_MENTORING', 'BUSINESS', 'EVENT', 'CUSTOM']
export const ALL_BADGES: NetworkingBadge[] = ['AVAILABLE_NOW', 'TOP_RATED', 'NEW', 'FEATURED']
export const DURATION_CHOICES = [10, 15, 20, 30, 45, 60, 90]

/** The six example categories (`label: null` = translated by the API and by `categoryLabel`). */
const DEFAULT_CATEGORIES: NetworkingCategory[] = [
  { key: 'career', icon: 'briefcase', label: null },
  { key: 'leadership', icon: 'users', label: null },
  { key: 'product', icon: 'package', label: null },
  { key: 'engineering', icon: 'code', label: null },
  { key: 'design', icon: 'palette', label: null },
  { key: 'entrepreneurship', icon: 'rocket', label: null }
]

const base = (): NetworkingConfig => ({
  preset: 'COMMUNITY_MENTORING',
  sides: 'MENTORING',
  sideLabels: { host: null, participant: null },
  offerers: 'MEMBERS',
  hostJoin: 'APPROVAL',
  participantJoin: 'OPEN',
  durations: [15, 30, 45, 60],
  defaultDuration: 30,
  groupSessions: { enabled: true, capacity: 20 },
  longTerm: true,
  requestApproval: 'HOST_CHOOSES',
  limits: { sessionsPerWeek: 2, sessionsPerPairPerMonth: 2, minNoticeHours: 12, horizonDays: 30 },
  rating: 'MUTUAL',
  reviewsVisibility: 'COMMUNITY',
  taxonomy: { categories: DEFAULT_CATEGORIES.map((c) => ({ ...c })), fieldIds: [] },
  badges: { enabled: [...ALL_BADGES], topRated: { minRating: 4.8, minReviews: 5 } },
  insights: true,
  suggestions: false,
  location: 'ONLINE'
})

const NO_TOP_RATED: NetworkingBadge[] = ['AVAILABLE_NOW', 'NEW', 'FEATURED']

/**
 * What each model fills (PROPOSTA-NETWORKING section 2.5), the same table as
 * the API's `validators/networkingConfig.js`. The model only fills: every
 * option can be changed afterwards.
 */
export const presetConfig = (preset: NetworkingPreset): NetworkingConfig => {
  const b = base()
  switch (preset) {
    case 'COMPANY_MENTORING':
      return { ...b, preset, hostJoin: 'INVITE', defaultDuration: 45, reviewsVisibility: 'HOST_AND_TEAM', badges: { ...b.badges, enabled: [...NO_TOP_RATED] }, insights: false, location: 'HOST_CHOOSES' }
    case 'BUSINESS':
      return {
        ...b, preset, sides: 'SYMMETRIC', hostJoin: 'OPEN', durations: [20], defaultDuration: 20, groupSessions: { enabled: false, capacity: 20 },
        longTerm: false, requestApproval: 'ALWAYS', reviewsVisibility: 'TEAM', badges: { ...b.badges, enabled: [...NO_TOP_RATED] }, insights: false, suggestions: true
      }
    case 'EVENT':
      return {
        ...b, preset, sides: 'SYMMETRIC', hostJoin: 'OPEN', durations: [15], defaultDuration: 15, groupSessions: { enabled: false, capacity: 20 },
        longTerm: false, requestApproval: 'NEVER', rating: 'ATTENDANCE_ONLY', reviewsVisibility: 'TEAM',
        badges: { ...b.badges, enabled: [...NO_TOP_RATED] }, insights: false, suggestions: true, location: 'IN_PERSON'
      }
    default:
      return { ...b, preset }
  }
}

export const defaultNetworkingConfig = (): NetworkingConfig => presetConfig('COMMUNITY_MENTORING')

/** A stored (maybe partial) config completed by its model, like the API's `resolveNetworkingConfig`. */
export const resolveConfig = (stored: Partial<NetworkingConfig> | null | undefined): NetworkingConfig => {
  const preset = stored?.preset && PRESETS.includes(stored.preset) ? stored.preset : 'COMMUNITY_MENTORING'
  const def = presetConfig(preset)
  const s = stored || {}
  return {
    ...def,
    ...s,
    preset,
    sideLabels: { ...def.sideLabels, ...(s.sideLabels || {}) },
    groupSessions: { ...def.groupSessions, ...(s.groupSessions || {}) },
    limits: { ...def.limits, ...(s.limits || {}) },
    taxonomy: { ...def.taxonomy, ...(s.taxonomy || {}) },
    badges: { ...def.badges, ...(s.badges || {}), topRated: { ...def.badges.topRated, ...(s.badges?.topRated || {}) } }
  }
}

export type NetworkingConfigErrors = Record<string, string>

/** `{ param: 'moduleConfig.networking.limits.sessionsPerWeek', message }` into `{ 'limits.sessionsPerWeek': message }`. */
export const networkingConfigApiErrors = (err: unknown): NetworkingConfigErrors | null => {
  const list = (err as ApiError | null)?.response?.data?.errors ?? []
  const out: NetworkingConfigErrors = {}
  for (const e of list) {
    const match = e.param?.match(/^moduleConfig\.networking\.(.+)$/)
    if (match && e.message && !out[match[1]]) out[match[1]] = e.message
  }
  return Object.keys(out).length ? out : null
}

/** Client checks before the API: only what the screen can get wrong. */
export const validateNetworkingConfig = (config: NetworkingConfig, t: TFunction): NetworkingConfigErrors | null => {
  const out: NetworkingConfigErrors = {}
  if (!config.durations.length) out.durations = t('networking:config.errors.durations')
  else if (!config.durations.includes(config.defaultDuration)) out.defaultDuration = t('networking:config.errors.defaultDuration')
  const keys = new Set<string>()
  config.taxonomy.categories.forEach((c, i) => {
    if (c.label !== null && !c.label.trim()) out[`taxonomy.categories[${i}].key`] = t('networking:config.errors.categoryName')
    else if (!/^[a-z0-9_-]{1,40}$/.test(c.key) || keys.has(c.key)) out[`taxonomy.categories[${i}].key`] = t('networking:config.errors.categoryKey')
    keys.add(c.key)
  })
  if (!(config.limits.horizonDays >= 1 && config.limits.horizonDays <= 365)) out['limits.horizonDays'] = t('networking:config.errors.horizon')
  return Object.keys(out).length ? out : null
}

/** A category's text: the edited label, or the translated example. */
export const categoryLabel = (t: TFunction, category: { key: string; label: string | null }): string =>
  category.label || t(`networking:defaultCategories.${category.key}`, { defaultValue: category.key })

/** "Mentor"/"Mentorado" (or "Anfitrião"/"Participante" in a symmetric program) when not edited. */
export const sideLabel = (t: TFunction, config: Pick<NetworkingConfig, 'sides' | 'sideLabels'>, side: 'host' | 'participant'): string =>
  config.sideLabels?.[side] || t(`networking:sides.${config.sides}.${side}`)

/** A key out of a typed label ("Vendas B2B" → "vendas-b2b"). */
export const keyOf = (label: string): string =>
  label.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
